import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, ScanLine, ClipboardList, ClipboardCheck, RefreshCw, Filter,
  Camera, X, CheckCircle2, PauseCircle,
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { useApp } from "@/context/AppContext";
import {
  securityLookup, securityAwaiting, securityHistory,
  securityApprove, securityHold,
} from "@/lib/api";

const HOLD_REASONS = ["Box Count Mismatch", "Item Mismatch", "Document Missing", "Vehicle Details Missing", "Other"];

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yy = dt.getFullYear();
    let h = dt.getHours(); const mn = String(dt.getMinutes()).padStart(2, "0");
    const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    return `${dd}/${mm}/${yy} ${String(h).padStart(2, "0")}:${mn} ${ap}`;
  } catch { return iso; }
};

// ==================== Scan Dispatch ====================
export const SecurityScan = () => {
  const { currentUser } = useApp();
  const isGuard = currentUser?.role === "security";
  const [slipInput, setSlipInput] = useState("");
  const [slip, setSlip] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const scannerRef = useRef(null);
  const startingRef = useRef(false);

  const doLookup = useCallback(async (id) => {
    if (!id || !id.trim()) return toast.error("Enter a Packing Slip ID.");
    setBusy(true); setSlip(null);
    try {
      const d = await securityLookup(id.trim());
      setSlip(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Packing Slip Not Found.");
    } finally { setBusy(false); }
  }, []);

  const stopCamera = useCallback(async () => {
    try {
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch {/* already stopped */}
        try { await scannerRef.current.clear(); } catch {/* ignore */}
        scannerRef.current = null;
      }
    } catch {/* ignore */}
    setScanning(false);
  }, []);

  const startCamera = useCallback(async () => {
    if (scannerRef.current || startingRef.current) return; // already running or in-flight
    startingRef.current = true;
    // Set scanning=true so the qr-reader div renders, then wait (poll) for it in the DOM.
    setScanning(true);
    let el = null;
    for (let i = 0; i < 20 && !el; i++) {           // up to ~1 s
      await new Promise((r) => setTimeout(r, 50));
      el = document.getElementById("qr-reader");
    }
    if (!el) {
      // Race with an unmount / navigation — silently bail.
      startingRef.current = false;
      setScanning(false);
      return;
    }
    // 3) Check that browser supports getUserMedia (needs HTTPS or localhost)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      startingRef.current = false;
      setScanning(false);
      toast.error("Camera API unavailable. Open this page over HTTPS on a device with a camera.");
      return;
    }
    try {
      const html5QrCode = new Html5Qrcode("qr-reader", /* verbose */ false);
      scannerRef.current = html5QrCode;
      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (text) => {
          await stopCamera();
          setSlipInput(text);
          doLookup(text);
        },
        () => {},
      );
    } catch (e) {
      scannerRef.current = null;
      setScanning(false);
      const name = e?.name || "";
      let msg = e?.message || "Unable to open camera. Please use manual entry.";
      if (name === "NotAllowedError" || /permission/i.test(msg)) {
        msg = "Camera permission denied. Please allow camera access in your browser settings.";
      } else if (name === "NotFoundError" || /no camera|not found/i.test(msg)) {
        msg = "No camera detected on this device.";
      } else if (name === "NotReadableError" || /in use|busy/i.test(msg)) {
        msg = "Camera is already in use by another app. Close it and try again.";
      }
      toast.error(msg);
    } finally {
      startingRef.current = false;
    }
  }, [doLookup, stopCamera]);

  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  // Auto-launch camera for guards so they don't need to press any button.
  useEffect(() => {
    if (isGuard && !slip && !scanning && !scannerRef.current) {
      startCamera().catch(() => {/* silent */});
    }
  }, [isGuard, slip, scanning, startCamera]);

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="security-scan-page">
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-xl bg-yamaha-50 flex items-center justify-center">
          <ScanLine className="w-8 h-8 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[26px] font-semibold text-yamaha-800 leading-none tracking-tight">Scan Dispatch</h1>
          <p className="text-[13.5px] text-[#747684] mt-1.5">
            Point your camera at the Packing Slip QR — or type the ID below.
          </p>
        </div>
      </div>

      {/* Big camera area */}
      <div className="bg-white border border-[#e2e1eb] rounded-2xl p-5 space-y-4 shadow-sm">
        {scanning ? (
          <>
            <div id="qr-reader" data-testid="security-scan-reader"
              style={{ width: "100%", maxWidth: 420, margin: "0 auto", borderRadius: 12, overflow: "hidden" }} />
            <button onClick={stopCamera}
              className="w-full py-3 rounded-xl border-2 border-rose-200 text-rose-700 text-[15px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-rose-50">
              <X className="w-4 h-4" /> Stop Camera
            </button>
          </>
        ) : (
          <button onClick={startCamera} data-testid="security-scan-camera"
            className="w-full py-6 rounded-xl bg-yamaha-700 hover:bg-yamaha-800 text-white text-[17px] font-semibold inline-flex items-center justify-center gap-2 shadow">
            <Camera className="w-6 h-6" /> Open Camera to Scan
          </button>
        )}

        <div className="flex items-center gap-2 text-[11.5px] text-[#a09fb0]">
          <div className="flex-1 h-px bg-[#e2e1eb]" />
          <span>OR</span>
          <div className="flex-1 h-px bg-[#e2e1eb]" />
        </div>

        <div>
          <label className="text-[11.5px] font-semibold uppercase tracking-wider text-[#747684] block mb-1.5">Enter Packing Slip ID</label>
          <div className="flex gap-2">
            <input data-testid="security-scan-input" value={slipInput} onChange={(e) => setSlipInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doLookup(slipInput)}
              placeholder="e.g. PS-2026-00125" autoFocus={!isGuard}
              className="flex-1 border border-[#e2e1eb] rounded-lg px-4 py-3 text-[15px] font-mono" />
            <button onClick={() => doLookup(slipInput)} disabled={busy} data-testid="security-scan-submit"
              className="px-5 py-3 rounded-lg bg-yamaha-700 hover:bg-yamaha-800 disabled:opacity-50 text-white text-[14px] font-semibold whitespace-nowrap">
              {busy ? "Looking…" : "Lookup"}
            </button>
          </div>
        </div>
      </div>

      {slip && <SlipCard slip={slip}
        onApprove={() => setApproveOpen(true)}
        onHold={() => setHoldOpen(true)} />}

      {approveOpen && slip && (
        <ApproveDialog slip={slip} onClose={() => setApproveOpen(false)}
          onDone={(fresh) => { setApproveOpen(false); setSlip(fresh); }} />
      )}
      {holdOpen && slip && (
        <HoldDialog slip={slip} onClose={() => setHoldOpen(false)}
          onDone={(fresh) => { setHoldOpen(false); setSlip(fresh); }} />
      )}
    </div>
  );
};

// ==================== Slip Card ====================
const StatusBadge = ({ status, cancelled }) => {
  if (cancelled) return <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">Cancelled</span>;
  const map = {
    dispatched: { l: "Dispatched — Security Verified", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    on_hold: { l: "Dispatch Held", c: "bg-rose-50 text-rose-700 border-rose-200" },
    awaiting_security_check: { l: "Awaiting Security Check", c: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const m = map[status] || map.awaiting_security_check;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.c}`}>{m.l}</span>;
};

const SlipCard = ({ slip, onApprove, onHold }) => {
  const editable = !slip.cancelled && slip.dispatch_status !== "dispatched";
  return (
    <div className="bg-white border border-[#e2e1eb] rounded-xl p-5 space-y-4" data-testid="security-slip-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#747684]">Packing Slip</div>
          <div className="text-[20px] font-mono font-semibold text-yamaha-700">{slip.slip_number}</div>
        </div>
        <StatusBadge status={slip.dispatch_status} cancelled={slip.cancelled} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="Source Location" value={slip.location_code} mono />
        <Field label="Destination" value={slip.to_location_name} />
        <Field label="Packing Date & Time" value={fmtDT(slip.packing_date)} />
        <Field label="Total Line Items" value={slip.total_line_items} />
        <Field label="Total Quantity" value={slip.total_quantity} />
        <Field label="Created By" value={slip.created_by} />
        <Field label="Vehicle Number" value={slip.vehicle_number || "—"} mono />
        <Field label="Invoice No" value={slip.invoice_no} mono />
      </div>

      {/* HIGHLIGHTED: Number of Boxes — the field security must verify first */}
      <div
        data-testid="slip-highlight-boxes"
        className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex items-center justify-between shadow-inner"
      >
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800">
            Number of Boxes — Physically Verify
          </div>
          <div className="text-[12px] text-amber-700 mt-0.5">
            Count the boxes on the vehicle and match with the slip.
          </div>
        </div>
        <div className="text-right">
          <div className="text-[46px] leading-none font-extrabold text-amber-900 tabular-nums">
            {slip.num_boxes ?? "—"}
          </div>
          <div className="text-[10.5px] uppercase tracking-wider text-amber-700 mt-1">Expected on slip</div>
        </div>
      </div>

      {slip.dispatch_status === "dispatched" && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-[12px] text-emerald-800">
          <div className="font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Already Dispatched</div>
          <div className="mt-1">By {slip.dispatch_security_user} at {fmtDT(slip.dispatch_at)} · Gate {slip.dispatch_gate || "—"} · Vehicle {slip.dispatch_vehicle_number || "—"}</div>
          <div className="mt-0.5">
            Verified Boxes: <b>{slip.dispatch_verified_num_boxes ?? "—"}</b>
            {slip.dispatch_expected_num_boxes != null && <> (Expected: <b>{slip.dispatch_expected_num_boxes}</b>)</>}
            {" · "}Signatures: <b>{slip.dispatch_signatures_verified ? "Verified" : "—"}</b>
          </div>
          {slip.dispatch_remarks && <div className="mt-0.5 italic">&ldquo;{slip.dispatch_remarks}&rdquo;</div>}
        </div>
      )}

      {slip.dispatch_status === "on_hold" && (
        <div className="border border-rose-200 bg-rose-50 rounded-lg p-3 text-[12px] text-rose-800">
          <div className="font-semibold flex items-center gap-1"><PauseCircle className="w-3.5 h-3.5" /> On Hold — {slip.hold_reason}</div>
          <div className="mt-1">By {slip.hold_by} at {fmtDT(slip.hold_at)}</div>
          {slip.hold_remarks && <div className="mt-0.5 italic">&ldquo;{slip.hold_remarks}&rdquo;</div>}
        </div>
      )}

      {editable && (
        <div className="flex flex-col sm:flex-row gap-2 pt-3">
          <button data-testid="security-hold-btn" onClick={onHold}
            className="flex-1 px-4 py-3.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border-2 border-rose-200 text-[15px] font-semibold inline-flex items-center justify-center gap-2">
            <PauseCircle className="w-5 h-5" /> Hold Dispatch
          </button>
          <button data-testid="security-approve-btn" onClick={onApprove}
            className="flex-1 px-4 py-3.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[15px] font-semibold inline-flex items-center justify-center gap-2 shadow">
            <CheckCircle2 className="w-5 h-5" /> Approve Dispatch
          </button>
        </div>
      )}
    </div>
  );
};
const Field = ({ label, value, mono = false }) => (
  <div>
    <div className="text-[10.5px] uppercase tracking-wider text-[#747684] font-semibold">{label}</div>
    <div className={`text-[12.5px] text-[#1a1b22] mt-0.5 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
  </div>
);

// ==================== Approve / Hold Dialogs ====================
const ApproveDialog = ({ slip, onClose, onDone }) => {
  const [gate, setGate] = useState("");
  const [vehicle, setVehicle] = useState(slip.vehicle_number || "");
  const [remarks, setRemarks] = useState("");
  const [verifiedBoxes, setVerifiedBoxes] = useState(
    slip.num_boxes != null ? String(slip.num_boxes) : ""
  );
  const [signatures, setSignatures] = useState(""); // "yes" | "no"
  const [busy, setBusy] = useState(false);

  const parsedBoxes = Number(verifiedBoxes);
  const boxesValid = Number.isFinite(parsedBoxes) && parsedBoxes > 0;
  const expectedBoxes = slip.num_boxes ?? null;
  const boxesMismatch = boxesValid && expectedBoxes != null && parsedBoxes !== Number(expectedBoxes);

  const canSubmit = boxesValid && signatures === "yes" && !boxesMismatch && !busy;

  const submit = async () => {
    if (!boxesValid) return toast.error("Enter a valid No of Boxes (> 0).");
    if (boxesMismatch) return toast.error(
      `Box count mismatch — entered ${parsedBoxes}, slip has ${expectedBoxes}. Use Hold Dispatch.`
    );
    if (signatures !== "yes") return toast.error("Two-Signatures verification is required (Yes).");
    setBusy(true);
    try {
      const r = await securityApprove(slip.slip_number, {
        gate: gate.trim(),
        vehicle_number: vehicle.trim(),
        remarks: remarks.trim(),
        verified_num_boxes: parsedBoxes,
        signatures_verified: true,
      });
      toast.success("Dispatch approved.");
      onDone(r.slip);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Approve Dispatch — Physical Verification" onClose={onClose} testId="security-approve-dialog">
      {/* No of Boxes — required physical verification */}
      <FormRow label="No of Boxes (Physically Verified) *">
        <div className="flex items-center gap-2">
          <input
            data-testid="security-approve-boxes"
            type="number" min="1" inputMode="numeric"
            value={verifiedBoxes}
            onChange={(e) => setVerifiedBoxes(e.target.value)}
            className="w-32 border-2 border-amber-300 bg-amber-50 rounded-md px-3 py-2 text-[18px] font-bold text-amber-900 tabular-nums"
            placeholder="0"
          />
          {expectedBoxes != null && (
            <span className="text-[11.5px] text-[#747684]">Expected: <b className="text-[#1a1b22]">{expectedBoxes}</b></span>
          )}
        </div>
        {boxesMismatch && (
          <div data-testid="security-approve-boxes-mismatch"
            className="mt-1.5 text-[12px] text-rose-800 bg-rose-50 border-2 border-rose-300 rounded px-2.5 py-2 font-semibold">
            Box count mismatch — you entered <b>{parsedBoxes}</b> but the slip expects <b>{expectedBoxes}</b>. Dispatch cannot be approved. Use <b>Hold Dispatch</b> with reason &ldquo;Box Count Mismatch&rdquo;.
          </div>
        )}
      </FormRow>

      {/* Two Signatures — Yes / No */}
      <FormRow label="Two Signatures Verified? *">
        <div className="flex gap-2" data-testid="security-approve-signatures">
          <button type="button" data-testid="security-sig-yes"
            onClick={() => setSignatures("yes")}
            className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
              signatures === "yes"
                ? "bg-emerald-600 border-emerald-600 text-white shadow"
                : "bg-white border-[#e2e1eb] text-[#444653] hover:bg-emerald-50 hover:border-emerald-300"
            }`}>
            <CheckCircle2 className="w-4 h-4" /> Yes — Both Signatures Present
          </button>
          <button type="button" data-testid="security-sig-no"
            onClick={() => setSignatures("no")}
            className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
              signatures === "no"
                ? "bg-rose-600 border-rose-600 text-white shadow"
                : "bg-white border-[#e2e1eb] text-[#444653] hover:bg-rose-50 hover:border-rose-300"
            }`}>
            <X className="w-4 h-4" /> No — Missing
          </button>
        </div>
        {signatures === "no" && (
          <div data-testid="security-approve-sig-warn"
            className="mt-1.5 text-[11.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
            Signatures missing — dispatch cannot be approved. Please use <b>Hold Dispatch</b> with reason &ldquo;Document Missing&rdquo;.
          </div>
        )}
      </FormRow>

      <FormRow label="Gate / Location"><input data-testid="security-approve-gate" value={gate} onChange={(e) => setGate(e.target.value)}
        className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" placeholder="e.g. Main Gate" /></FormRow>
      <FormRow label="Vehicle Number"><input data-testid="security-approve-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)}
        className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] font-mono uppercase" placeholder="AP01AB1234" /></FormRow>
      <FormRow label="Remarks"><textarea data-testid="security-approve-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
        className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" /></FormRow>

      <Actions onCancel={onClose} onConfirm={submit} busy={busy}
        confirmLabel="Confirm Dispatch"
        confirmDisabled={!canSubmit}
        confirmTestId="security-approve-confirm" tone="emerald" icon={<CheckCircle2 className="w-3.5 h-3.5" />} />
    </ModalShell>
  );
};
const HoldDialog = ({ slip, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) return toast.error("Reason is required.");
    if (!remarks.trim()) return toast.error("Remarks are required.");
    setBusy(true);
    try {
      const r = await securityHold(slip.slip_number, { reason, remarks: remarks.trim() });
      toast.success("Dispatch held.");
      onDone(r.slip);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hold failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Hold Dispatch" onClose={onClose} testId="security-hold-dialog">
      <FormRow label="Reason *">
        <select data-testid="security-hold-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]">
          <option value="">— Select —</option>
          {HOLD_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </FormRow>
      <FormRow label="Remarks *"><textarea data-testid="security-hold-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3}
        className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]" /></FormRow>
      <Actions onCancel={onClose} onConfirm={submit} busy={busy}
        confirmLabel="Confirm Hold" confirmTestId="security-hold-confirm" tone="rose" icon={<PauseCircle className="w-3.5 h-3.5" />} />
    </ModalShell>
  );
};

const ModalShell = ({ title, onClose, children, testId }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid={testId}>
    <div className="bg-white rounded-xl border border-[#e2e1eb] w-full max-w-md shadow-xl">
      <div className="px-5 py-3 border-b border-[#e2e1eb] flex items-start gap-2">
        <h3 className="flex-1 text-[15px] font-semibold text-[#1a1b22]">{title}</h3>
        <button onClick={onClose} className="text-[#747684] hover:text-[#1a1b22]"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-5 space-y-3">{children}</div>
    </div>
  </div>
);
const FormRow = ({ label, children }) => (
  <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">{label}</label>{children}</div>
);
const Actions = ({ onCancel, onConfirm, busy, confirmLabel, confirmTestId, tone, icon, confirmDisabled = false }) => {
  const tones = { emerald: "bg-emerald-600 hover:bg-emerald-700", rose: "bg-rose-600 hover:bg-rose-700" };
  return (
    <div className="pt-2 flex justify-end gap-2">
      <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-[#e2e1eb] text-[12px]">Cancel</button>
      <button onClick={onConfirm} disabled={busy || confirmDisabled} data-testid={confirmTestId}
        className={`px-4 py-2 rounded-lg ${tones[tone] || tones.emerald} disabled:opacity-50 disabled:cursor-not-allowed text-white text-[12px] font-semibold inline-flex items-center gap-1.5`}>
        {icon} {busy ? "Saving…" : confirmLabel}
      </button>
    </div>
  );
};

// ==================== Awaiting Dispatch ====================
export const SecurityAwaiting = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await securityAwaiting(); setRows(d.rows || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="security-awaiting-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <ClipboardList className="w-6 h-6 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold text-yamaha-800 leading-none tracking-tight">Awaiting Dispatch</h1>
          <p className="text-[12.5px] text-[#747684] mt-1">{rows.length} packing slip{rows.length === 1 ? "" : "s"} pending security verification.</p>
        </div>
        <button onClick={load} data-testid="security-awaiting-refresh"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <DispatchTable rows={rows} loading={loading} tab="awaiting" />
    </div>
  );
};

// ==================== Dispatch History ====================
export const SecurityHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await securityHistory({ status: status || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined });
      setRows(d.rows || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, [status, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="security-history-page">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <ClipboardCheck className="w-6 h-6 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold text-yamaha-800 leading-none tracking-tight">Dispatch History</h1>
          <p className="text-[12.5px] text-[#747684] mt-1">All security dispatch activity, latest first.</p>
        </div>
        <button onClick={load} data-testid="security-history-refresh"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#747684]" />
        <select data-testid="security-hist-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          <option value="">All</option>
          <option value="awaiting_security_check">Awaiting</option>
          <option value="dispatched">Dispatched</option>
          <option value="on_hold">On Hold</option>
        </select>
        <input type="date" data-testid="security-hist-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
        <span className="text-[11px] text-[#747684]">to</span>
        <input type="date" data-testid="security-hist-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
      </div>

      <DispatchTable rows={rows} loading={loading} tab="history" />
    </div>
  );
};

const DispatchTable = ({ rows, loading, tab }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid={`security-${tab}-table`}>
        <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left">Slip #</th>
            <th className="px-3 py-2 text-left">Source</th>
            <th className="px-3 py-2 text-left">Destination</th>
            <th className="px-3 py-2 text-left">Created</th>
            <th className="px-3 py-2 text-center">Items</th>
            <th className="px-3 py-2 text-center">Qty</th>
            <th className="px-3 py-2 text-left">Vehicle</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Action Time</th>
            <th className="px-3 py-2 text-left">By</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#eeecf6]">
          {rows.length === 0 && (
            <tr><td colSpan={10} className="text-center text-[12.5px] text-[#747684] py-10">
              {loading ? "Loading…" : "No packing slips to show."}
            </td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} data-testid={`security-row-${r.id}`} className="hover:bg-[#faf8ff]">
              <td className="px-3 py-2 font-mono font-semibold text-yamaha-700">{r.slip_number}</td>
              <td className="px-3 py-2 font-mono">{r.location_code}</td>
              <td className="px-3 py-2">{r.to_location_name || "—"}</td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDT(r.packing_date)}</td>
              <td className="px-3 py-2 text-center">{r.total_line_items}</td>
              <td className="px-3 py-2 text-center">{r.total_quantity}</td>
              <td className="px-3 py-2 font-mono">{r.dispatch_vehicle_number || "—"}</td>
              <td className="px-3 py-2"><StatusBadge status={r.dispatch_status} cancelled={r.cancelled} /></td>
              <td className="px-3 py-2 whitespace-nowrap">{fmtDT(r.dispatch_at || r.hold_at)}</td>
              <td className="px-3 py-2 text-[#747684]">{r.dispatch_security_user || r.hold_by || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default SecurityScan;
